import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { LOAN_AGREEMENT_STATUSES, outstandingItems } from "@/contracts";

import { loanAgreementsQuery, type LoanAgreementFilters } from "@/api/queries";
import { hasRole, useAuth } from "@/auth/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { SelectField } from "@/components/ui/field";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { OrganizationFilter } from "@/features/organizations/organization-filter";
import { useApiClient } from "@/lib/api-context";
import { useListFilters } from "@/lib/use-list-filters";
import { EM_DASH, formatDate } from "@/lib/format";
import { LoanAgreementCreateDialog } from "./loan-agreement-create-dialog";
import { LOAN_STATUS_LABELS } from "./loan-labels";
import { LoanStatusBadge } from "./loan-status-badge";

/** Soft highlight window for the Ends column, matching the 30-day filter option. */
const ENDING_SOON_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * The loan agreements: which equipment is out, with whom, and on what terms.
 *
 * The column that earns its place is "Out": a sensor still in somebody's
 * cold room after the term ended is the thing this screen exists to surface.
 *
 * When `embedded`, the contracts hub owns the page title; this panel only
 * renders filters, the table and the draw-up action.
 */
export function LoanAgreementsPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation("loans");
  const client = useApiClient();
  const { user } = useAuth();
  const canDraw = hasRole(user, ["ADMIN", "SUPER_ADMIN"]);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const { value: filterValue, page, setFilter, setPage } = useListFilters();
  const [drawing, setDrawing] = useState(false);
  // Captured once per mount so the Ends column highlight stays pure under React's
  // render rules; a full remount (navigation away and back) refreshes the clock.
  const [nowMs] = useState(() => Date.now());

  // Read once and narrowed once: a type predicate applied to a function call
  // does not narrow the next call to the same function.
  const status = filterValue("status");
  const endingWithin = filterValue("endingWithinDays");

  const filters: LoanAgreementFilters = {
    ...(filterValue("search") ? { search: filterValue("search") } : {}),
    ...(filterValue("organizationId") ? { organizationId: filterValue("organizationId") } : {}),
    ...(isStatus(status) ? { status } : {}),
    ...(endingWithin !== "" && Number.isFinite(Number(endingWithin))
      ? { endingWithinDays: Number(endingWithin) }
      : {}),
    page,
  };

  const agreements = useQuery(loanAgreementsQuery(client, filters));
  const columnCount = isSuperAdmin ? 8 : 7;
  const drawAction = canDraw ? (
    <Button onClick={() => setDrawing(true)}>{t("drawUp")}</Button>
  ) : null;

  return (
    <>
      {embedded ? (
        drawAction ? <div className="mb-4 flex justify-end">{drawAction}</div> : null
      ) : (
        <PageHeader
          title={t("listTitle")}
          description={t("listDescription")}
          action={drawAction}
        />
      )}

      <Card className="mb-4">
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SearchInput
              value={filterValue("search")}
              placeholder={t("searchPlaceholder")}
              onChange={(value) => setFilter("search", value)}
            />

            <SelectField
              label={t("statusFilter")}
              value={status}
              onChange={(value) => setFilter("status", value)}
              data={[
                { value: "", label: t("anyStatus") },
                ...LOAN_AGREEMENT_STATUSES.map((statusOption) => ({
                  value: statusOption,
                  label: LOAN_STATUS_LABELS[statusOption],
                })),
              ]}
            />

            <SelectField
              label={t("endingWithin")}
              value={endingWithin}
              onChange={(value) => setFilter("endingWithinDays", value)}
              data={[
                { value: "", label: t("endingAny") },
                { value: "0", label: t("endingDue") },
                { value: "30", label: t("ending30") },
                { value: "60", label: t("ending60") },
                { value: "90", label: t("ending90") },
              ]}
            />

            {isSuperAdmin ? (
              <OrganizationFilter
                value={filterValue("organizationId")}
                onChange={(value) => setFilter("organizationId", value)}
              />
            ) : null}
          </div>
        </CardBody>
      </Card>

      {agreements.isPending ? <LoadingBlock label={t("loading")} /> : null}

      {agreements.isError ? (
        <ErrorBlock error={agreements.error} onRetry={() => void agreements.refetch()} />
      ) : null}

      {agreements.data ? (
        <Card>
          <DataTable caption={t("listTitle")}>
            <thead>
              <tr>
                {isSuperAdmin ? <Th>{t("colOrganization")}</Th> : null}
                <Th>{t("colNumber")}</Th>
                <Th>{t("colBorrower")}</Th>
                <Th>{t("colSite")}</Th>
                <Th>{t("colStatus")}</Th>
                <Th numeric>{t("colUnits")}</Th>
                <Th numeric>{t("colOut")}</Th>
                <Th>{t("colEnds")}</Th>
              </tr>
            </thead>

            <tbody>
              {agreements.data.data.length === 0 ? (
                <EmptyRow colSpan={columnCount}>{t("emptyList")}</EmptyRow>
              ) : null}

              {agreements.data.data.map((agreement) => {
                const out = outstandingItems(agreement.items);
                const endsAt = new Date(agreement.endsOn).getTime();
                const endingSoon =
                  (agreement.status === "ISSUED" || agreement.status === "SIGNED") &&
                  endsAt <= nowMs + ENDING_SOON_MS;

                return (
                  <tr key={agreement.id}>
                    {isSuperAdmin ? <Td>{agreement.organizationName}</Td> : null}
                    <Td className="font-medium">
                      <Link
                        to={`/loan-agreements/${agreement.id}`}
                        className="text-brand underline underline-offset-2"
                      >
                        {agreement.number}
                      </Link>
                    </Td>
                    <Td>{agreement.borrowerName}</Td>
                    <Td>{agreement.siteName}</Td>
                    <Td>
                      <LoanStatusBadge value={agreement.status} />
                    </Td>
                    <Td numeric>{agreement.items.length}</Td>
                    <Td numeric>{agreement.status === "CLOSED" ? EM_DASH : out}</Td>
                    <Td className={endingSoon ? "font-medium text-warn" : ""}>
                      {formatDate(agreement.endsOn)}
                      {endingSoon && endsAt < nowMs
                        ? ` · ${t("overdue")}`
                        : endingSoon
                          ? ` · ${t("soon")}`
                          : null}
                    </Td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot>
              <tr>
                <Td colSpan={columnCount} className="border-b-0">
                  <Pagination
                    page={agreements.data.pagination.page}
                    totalPages={agreements.data.pagination.totalPages}
                    total={agreements.data.pagination.total}
                    noun={{ one: "agreement", other: "agreements" }}
                    onChange={setPage}
                  />
                </Td>
              </tr>
            </tfoot>
          </DataTable>
        </Card>
      ) : null}

      <LoanAgreementCreateDialog open={drawing} onClose={() => setDrawing(false)} />
    </>
  );
}

function isStatus(value: string): value is (typeof LOAN_AGREEMENT_STATUSES)[number] {
  return (LOAN_AGREEMENT_STATUSES as readonly string[]).includes(value);
}
